<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">
                        Receita de Taxa (Mercado Pago)
                    </v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 12 meses"
                        @update="consultar"
                    />
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <!-- Cards -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(resumo.total_taxa / 100)}`"
                                    label="Taxa Arrecadada"
                                    color="green accent-4"
                                    icon="mdi-cash-plus"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="resumo.qtd_transacoes"
                                    label="Transações"
                                    color="blue darken-1"
                                    icon="mdi-swap-horizontal"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(resumo.ticket_medio_taxa / 100)}`"
                                    label="Ticket Médio de Taxa"
                                    color="amber lighten-1"
                                    icon="mdi-ticket-confirmation"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`${resumo.taxa_efetiva_percentual}%`"
                                    label="Taxa Efetiva"
                                    color="deep-purple accent-2"
                                    icon="mdi-percent"
                                />
                            </v-col>
                        </v-row>

                        <v-row v-if="porConta.length > 1">
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Taxa por conta
                                    </div>
                                    <v-card-text class="pt-0">
                                        <span
                                            v-for="linha in porConta"
                                            :key="linha.conta"
                                            class="mr-8"
                                        >
                                            <span class="font-weight-medium">{{ rotuloConta(linha.conta) }}:</span>
                                            R$ {{ formatPrice(linha.total_taxa / 100) }}
                                            <span class="grey--text">({{ linha.qtd_transacoes }} transações)</span>
                                        </span>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Receita de taxa mensal -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Receita de Taxa Mensal
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartTaxa" height="80"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Por empresa -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Por Empresa ({{ porEmpresa.length }})
                                    </div>
                                    <v-data-table
                                        :headers="headersEmpresa"
                                        :items="porEmpresa"
                                        :mobileBreakpoint="0"
                                        :items-per-page="15"
                                        dense
                                    >
                                        <template v-slot:item.total_processado="{ item }">
                                            R$ {{ formatPrice(item.total_processado / 100) }}
                                        </template>
                                        <template v-slot:item.total_taxa="{ item }">
                                            R$ {{ formatPrice(item.total_taxa / 100) }}
                                        </template>
                                        <template v-slot:item.empresa_ativa="{ item }">
                                            <v-chip :color="item.empresa_ativa ? 'success' : 'error'" x-small>
                                                {{ item.empresa_ativa ? 'Ativo' : 'Inativo' }}
                                            </v-chip>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Chart from 'chart.js';
import { CONTAS_MP, rotuloDe } from '@/views/cobrancas/status';

export default {
    name: 'RelatorioReceitaTaxa',
    components: { BaseContainer, Card, PeriodoFilter },

    data: () => ({
        periodo: {},
        loading: false,
        resumo: {
            qtd_transacoes: 0,
            total_taxa: 0,
            total_processado: 0,
            ticket_medio_taxa: 0,
            taxa_efetiva_percentual: 0,
            por_conta: [],
        },
        mensal: [],
        porEmpresa: [],
        chart: null,
        headersEmpresa: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'Transações', value: 'qtd_transacoes' },
            { text: 'Processado', value: 'total_processado' },
            { text: 'Taxa', value: 'total_taxa' },
            { text: 'Status', value: 'empresa_ativa' },
        ],
    }),

    computed: {
        porConta() {
            return this.resumo.por_conta || [];
        },
    },

    mounted() {
        this.$nextTick(() => this.consultar());
    },

    methods: {
        rotuloConta(valor) {
            return rotuloDe(CONTAS_MP, valor);
        },

        async consultar() {
            this.loading = true;
            const params = `start=${this.periodo.start || ''}&end=${this.periodo.end || ''}`;

            try {
                const [resumo, mensal, porEmpresa] = await Promise.all([
                    this.$http.get(`relatorios/receita-taxa/resumo?${params}`),
                    this.$http.get(`relatorios/receita-taxa/mensal?${params}`),
                    this.$http.get(`relatorios/receita-taxa/por-empresa?${params}`),
                ]);

                this.resumo = resumo.data.data;
                this.mensal = mensal.data.data;
                this.porEmpresa = porEmpresa.data.data;

                this.renderChart();
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar relatório de receita de taxa', 'warning');
            }

            this.loading = false;
        },

        renderChart() {
            if (this.chart) this.chart.destroy();
            const ctx = this.$refs.chartTaxa;
            if (!ctx) return;

            this.chart = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: this.mensal.map(r => r.mes),
                    datasets: [{
                        label: 'Receita de Taxa (R$)',
                        data: this.mensal.map(r => r.total_taxa / 100),
                        backgroundColor: 'rgba(76, 175, 80, 0.7)',
                        borderColor: 'rgba(76, 175, 80, 1)',
                        borderWidth: 1,
                    }],
                },
                options: {
                    responsive: true,
                    scales: {
                        yAxes: [{
                            ticks: {
                                beginAtZero: true,
                                callback: (value) => 'R$ ' + Number(value).toFixed(2),
                            },
                        }],
                    },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem) => 'R$ ' + Number(tooltipItem.yLabel).toFixed(2),
                        },
                    },
                },
            });
        },
    },
};
</script>
